import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { theme } from './styles/theme';
import GlobalStyles from './styles/GlobalStyles';
import Footer from './components/layout/Footer';
import LandingPage from './pages/LandingPage';
import AboutMePage from './pages/AboutMePage';
import ContactPage from './pages/ContactPage';
import ServicesPage from './pages/ServicesPage';
import EducationPage from './pages/EducationPage';
import EarthworkPage from './pages/education/EarthworkPage';
import BlogPostPage from './pages/BlogPostPage';
import ConstructionPage from './pages/ConstructionPage';
import PeriodicTablePage from './pages/PeriodicTablePage';

/**
 * The App component serves as the entry point for the application.
 * Updated: Added ScrollToTop component to automatically scroll to top on page navigation
 * Updated: Added route for the full-screen interactive periodic table
 * Updated: PeriodicTablePage is rendered outside of Layout for full-screen experience
 * Updated: Removed standalone Insights page, moved content to About Me page
 * Updated: Removed standalone Portfolio page, moved content to Landing page
 * It sets up React Router for client-side navigation and wraps all content
 * in the Layout component, which provides consistent structure with header and footer.
 *
 * Routes are defined for each page of the application:
 * - Landing/Home page at the root URL
 * - About page for professional bio and project highlights
 * - Contact page with form functionality
 * - Individual blog post pages with dynamic routing based on post ID
 * - Services page showcasing available services
 * - Education page for educational resources
 * - Periodic Table page with interactive periodic table
 * - Construction page for construction-related content
 */
const AppContent = () => {
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const isPeriodicTablePage = location.pathname === '/education/periodic-table';
  const isEducationPage = location.pathname === '/education';
  const isServicesPage = location.pathname === '/services';
  const isAboutPage = location.pathname === '/about';

  const content = (
    <Routes>
      <Route path="/" element={
        <>
          <LandingPage />
          <Footer />
        </>
      } />
      <Route path="/education/periodic-table" element={
        <>
          <PeriodicTablePage />
          <Footer />
        </>
      } />
      <Route path="/education" element={
        <>
          <EducationPage />
          <Footer />
        </>
      } />
      <Route path="/services" element={
        <>
          <ServicesPage />
          <Footer />
        </>
      } />
      <Route path="/about" element={
        <>
          <AboutMePage />
          <Footer />
        </>
      } />
      {!isLandingPage && !isPeriodicTablePage && !isEducationPage && !isServicesPage && !isAboutPage && (
        <>
          <Route path="/education/construction" element={
            <>
              <ConstructionPage />
              <Footer />
            </>
          } />
          <Route path="/education/construction/earthwork" element={
            <>
              <EarthworkPage />
              <Footer />
            </>
          } />
          <Route path="/contact" element={
            <>
              <ContactPage />
              <Footer />
            </>
          } />
          <Route path="/insights/:id" element={
            <>
              <BlogPostPage />
              <Footer />
            </>
          } />
        </>
      )}
    </Routes>
  );

  // Return content directly without Layout wrapper
  return content;
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
};

export default App;